import axios from "axios";
import { HttpCoreService } from "../services/HttpCoreService";
import { currentConfig } from "../config/environment";
import {
  SiteFeasibilityRecord,
  SiteFeasibilityListResponse,
  SiteFeasibilityFilters,
  CreateSiteFeasibilityRequest,
  UpdateSiteFeasibilityRequest,
  SiteFeasibilityAuditEntry,
  FeasibilityTemplate,
  FeasibilityTemplateListResponse,
  CreateFeasibilityTemplateRequest,
  FeasibilityRecordV2,
  FeasibilityRecordV2ListResponse,
  CreateFeasibilityRecordV2Request,
  UpdateFeasibilityRecordV2Request,
  FeasibilityResponseReport,
  FeasibilityReportListResponse,
  FeasibilityPublicRecord,
  SubmitQuestionnaireRequest,
} from "../types/siteFeasibility/siteFeasibility.type";

export interface ISiteFeasibilityRepository {
  list(filters?: SiteFeasibilityFilters): Promise<SiteFeasibilityListResponse>;
  getById(id: string): Promise<SiteFeasibilityRecord>;
  create(data: CreateSiteFeasibilityRequest): Promise<SiteFeasibilityRecord>;
  update(
    id: string,
    data: UpdateSiteFeasibilityRequest,
  ): Promise<SiteFeasibilityRecord>;
  delete(id: string): Promise<void>;
  uploadDocument(id: string, file: File): Promise<SiteFeasibilityRecord>;
  removeDocument(id: string): Promise<SiteFeasibilityRecord>;
  getAuditTrail(id: string): Promise<SiteFeasibilityAuditEntry[]>;

  // V2: Templates
  listTemplates(params?: {
    studyId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<FeasibilityTemplateListResponse>;
  getTemplate(id: string): Promise<FeasibilityTemplate>;
  createTemplate(
    data: CreateFeasibilityTemplateRequest,
  ): Promise<FeasibilityTemplate>;
  updateTemplate(
    id: string,
    data: Partial<CreateFeasibilityTemplateRequest>,
  ): Promise<FeasibilityTemplate>;
  activateTemplate(id: string): Promise<FeasibilityTemplate>;
  inactivateTemplate(id: string): Promise<FeasibilityTemplate>;
  cloneTemplate(id: string): Promise<FeasibilityTemplate>;
  newTemplateVersion(id: string): Promise<FeasibilityTemplate>;
  deleteTemplate(id: string): Promise<void>;

  // V2: Feasibility Records
  listRecordsV2(params?: {
    studyId?: string;
    page?: number;
    limit?: number;
    eligible?: boolean;
    activationReady?: boolean;
  }): Promise<FeasibilityRecordV2ListResponse>;
  getRecordV2(id: string): Promise<FeasibilityRecordV2>;
  createRecordV2(
    data: CreateFeasibilityRecordV2Request,
  ): Promise<FeasibilityRecordV2>;
  updateRecordV2(
    id: string,
    data: UpdateFeasibilityRecordV2Request,
  ): Promise<FeasibilityRecordV2>;
  sendEmail(id: string): Promise<FeasibilityRecordV2>;
  uploadFeasibilityDocument(
    id: string,
    file: File,
  ): Promise<FeasibilityRecordV2>;
  replaceDocument(id: string, file: File): Promise<FeasibilityRecordV2>;
  replaceVersion(id: string): Promise<FeasibilityRecordV2>;
  markEligibleV2(id: string): Promise<FeasibilityRecordV2>;
  markNotEligibleV2(id: string): Promise<FeasibilityRecordV2>;
  fileToETMF(id: string): Promise<FeasibilityRecordV2>;
  generateReport(id: string): Promise<FeasibilityRecordV2>;
  deleteRecordV2(id: string): Promise<void>;

  // V2: Response Reports
  listResponseReports(params?: {
    studyId?: string;
    page?: number;
    limit?: number;
  }): Promise<FeasibilityReportListResponse>;
  getResponseReport(id: string): Promise<FeasibilityResponseReport>;
  fileReportToETMF(id: string, file?: File): Promise<FeasibilityResponseReport>;
  replaceReportVersion(
    id: string,
    file: File,
    reason: string,
  ): Promise<FeasibilityResponseReport>;

  // Public (unauthenticated) — PI-facing questionnaire
  getPublicRecord(recordId: string): Promise<FeasibilityPublicRecord>;
  submitQuestionnaire(
    recordId: string,
    data: SubmitQuestionnaireRequest,
  ): Promise<FeasibilityRecordV2>;
}

export class SiteFeasibilityRepository implements ISiteFeasibilityRepository {
  // Separate axios instance without auth headers for public endpoints
  private static readonly publicClient = axios.create({
    baseURL: currentConfig.coreApiBaseUrl,
    timeout: 30000,
    headers: { "Content-Type": "application/json", Accept: "application/json" },
  });

  constructor(private readonly httpCoreService: HttpCoreService) {}

  async list(
    filters?: SiteFeasibilityFilters,
  ): Promise<SiteFeasibilityListResponse> {
    const params = new URLSearchParams();
    if (filters?.studyId) {
      params.set("studyId", filters.studyId);
    }
    if (filters?.page) {
      params.set("page", String(filters.page));
    }
    if (filters?.limit) {
      params.set("limit", String(filters.limit));
    }
    if (filters?.feasibilityDone) {
      params.set("feasibilityDone", filters.feasibilityDone);
    }
    if (filters?.siteEligible) {
      params.set("siteEligible", filters.siteEligible);
    }
    if (filters?.country) {
      params.set("country", filters.country);
    }
    if (filters?.search) {
      params.set("search", filters.search);
    }

    const query = params.toString();
    const url = `/site-feasibility${query ? `?${query}` : ""}`;
    const response =
      await this.httpCoreService.get<SiteFeasibilityListResponse>(url);

    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(
      response.message || "Failed to fetch site feasibility records",
    );
  }

  async getById(id: string): Promise<SiteFeasibilityRecord> {
    const response = await this.httpCoreService.get<SiteFeasibilityRecord>(
      `/site-feasibility/${id}`,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(
      response.message || "Failed to fetch site feasibility record",
    );
  }

  async create(
    data: CreateSiteFeasibilityRequest,
  ): Promise<SiteFeasibilityRecord> {
    const response = await this.httpCoreService.post<SiteFeasibilityRecord>(
      "/site-feasibility",
      data,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(
      response.message || "Failed to create site feasibility record",
    );
  }

  async update(
    id: string,
    data: UpdateSiteFeasibilityRequest,
  ): Promise<SiteFeasibilityRecord> {
    const response = await this.httpCoreService.patch<SiteFeasibilityRecord>(
      `/site-feasibility/${id}`,
      data,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(
      response.message || "Failed to update site feasibility record",
    );
  }

  async delete(id: string): Promise<void> {
    const response = await this.httpCoreService.delete(
      `/site-feasibility/${id}`,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return;
    }
    throw new Error(
      response.message || "Failed to delete site feasibility record",
    );
  }

  async uploadDocument(id: string, file: File): Promise<SiteFeasibilityRecord> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.httpCoreService.postForm<SiteFeasibilityRecord>(
      `/site-feasibility/${id}/document`,
      formData,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to upload document");
  }

  async removeDocument(id: string): Promise<SiteFeasibilityRecord> {
    const response = await this.httpCoreService.delete<SiteFeasibilityRecord>(
      `/site-feasibility/${id}/document`,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to remove document");
  }

  async getAuditTrail(id: string): Promise<SiteFeasibilityAuditEntry[]> {
    const response = await this.httpCoreService.get<
      SiteFeasibilityAuditEntry[]
    >(`/site-feasibility/${id}/audit-trail`);
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to fetch audit trail");
  }

  // ─── V2: Templates ──────────────────────────────────────────────────────────

  async listTemplates(params?: {
    studyId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<FeasibilityTemplateListResponse> {
    const query = new URLSearchParams();
    if (params?.studyId) {
      query.set("studyId", params.studyId);
    }
    if (params?.status) {
      query.set("status", params.status);
    }
    if (params?.page) {
      query.set("page", String(params.page));
    }
    if (params?.limit) {
      query.set("limit", String(params.limit));
    }
    const url = `/feasibility-templates${query.toString() ? `?${query.toString()}` : ""}`;
    const response =
      await this.httpCoreService.get<FeasibilityTemplateListResponse>(url);
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(
      response.message || "Failed to fetch feasibility templates",
    );
  }

  async getTemplate(id: string): Promise<FeasibilityTemplate> {
    const response = await this.httpCoreService.get<FeasibilityTemplate>(
      `/feasibility-templates/${id}`,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to fetch template");
  }

  async createTemplate(
    data: CreateFeasibilityTemplateRequest,
  ): Promise<FeasibilityTemplate> {
    const response = await this.httpCoreService.post<FeasibilityTemplate>(
      "/feasibility-templates",
      data,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to create template");
  }

  async updateTemplate(
    id: string,
    data: Partial<CreateFeasibilityTemplateRequest>,
  ): Promise<FeasibilityTemplate> {
    const response = await this.httpCoreService.patch<FeasibilityTemplate>(
      `/feasibility-templates/${id}`,
      data,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to update template");
  }

  async activateTemplate(id: string): Promise<FeasibilityTemplate> {
    const response = await this.httpCoreService.patch<FeasibilityTemplate>(
      `/feasibility-templates/${id}/activate`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to activate template");
  }

  async inactivateTemplate(id: string): Promise<FeasibilityTemplate> {
    const response = await this.httpCoreService.patch<FeasibilityTemplate>(
      `/feasibility-templates/${id}/inactivate`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to inactivate template");
  }

  async cloneTemplate(id: string): Promise<FeasibilityTemplate> {
    const response = await this.httpCoreService.post<FeasibilityTemplate>(
      `/feasibility-templates/${id}/clone`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to clone template");
  }

  async newTemplateVersion(id: string): Promise<FeasibilityTemplate> {
    const response = await this.httpCoreService.post<FeasibilityTemplate>(
      `/feasibility-templates/${id}/new-version`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to create new version");
  }

  async deleteTemplate(id: string): Promise<void> {
    const response = await this.httpCoreService.delete(
      `/feasibility-templates/${id}`,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return;
    }
    throw new Error(response.message || "Failed to delete template");
  }

  // ─── V2: Feasibility Records ────────────────────────────────────────────────

  async listRecordsV2(params?: {
    studyId?: string;
    page?: number;
    limit?: number;
    eligible?: boolean;
    activationReady?: boolean;
  }): Promise<FeasibilityRecordV2ListResponse> {
    const query = new URLSearchParams();
    if (params?.studyId) {
      query.set("studyId", params.studyId);
    }
    if (params?.page) {
      query.set("page", String(params.page));
    }
    if (params?.limit) {
      query.set("limit", String(params.limit));
    }
    if (params?.eligible !== undefined) {
      query.set("eligible", String(params.eligible));
    }
    if (params?.activationReady !== undefined) {
      query.set("activationReady", String(params.activationReady));
    }
    const url = `/feasibility-records${query.toString() ? `?${query.toString()}` : ""}`;
    const response =
      await this.httpCoreService.get<FeasibilityRecordV2ListResponse>(url);
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to fetch feasibility records");
  }

  async getRecordV2(id: string): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.get<FeasibilityRecordV2>(
      `/feasibility-records/${id}`,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to fetch feasibility record");
  }

  async createRecordV2(
    data: CreateFeasibilityRecordV2Request,
  ): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.post<FeasibilityRecordV2>(
      "/feasibility-records",
      data,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to create feasibility record");
  }

  async updateRecordV2(
    id: string,
    data: UpdateFeasibilityRecordV2Request,
  ): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.patch<FeasibilityRecordV2>(
      `/feasibility-records/${id}`,
      data,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to update feasibility record");
  }

  async sendEmail(id: string): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.post<FeasibilityRecordV2>(
      `/feasibility-records/${id}/send-email`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to send email");
  }

  async uploadFeasibilityDocument(
    id: string,
    file: File,
  ): Promise<FeasibilityRecordV2> {
    const formData = new FormData();
    formData.append("document", file);
    const response = await this.httpCoreService.postForm<FeasibilityRecordV2>(
      `/feasibility-records/${id}/upload-document`,
      formData,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to upload document");
  }

  async replaceDocument(id: string, file: File): Promise<FeasibilityRecordV2> {
    const formData = new FormData();
    formData.append("document", file);
    const response = await this.httpCoreService.postForm<FeasibilityRecordV2>(
      `/feasibility-records/${id}/replace-document`,
      formData,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to replace document");
  }

  async replaceVersion(id: string): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.post<FeasibilityRecordV2>(
      `/feasibility-records/${id}/replace-version`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to replace version");
  }

  async markEligibleV2(id: string): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.post<FeasibilityRecordV2>(
      `/feasibility-records/${id}/mark-eligible`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to mark as eligible");
  }

  async markNotEligibleV2(id: string): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.post<FeasibilityRecordV2>(
      `/feasibility-records/${id}/mark-not-eligible`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to mark as not eligible");
  }

  async fileToETMF(id: string): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.patch<FeasibilityRecordV2>(
      `/feasibility-records/${id}/file-to-etmf`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to file to eTMF");
  }

  async generateReport(id: string): Promise<FeasibilityRecordV2> {
    const response = await this.httpCoreService.post<FeasibilityRecordV2>(
      `/feasibility-records/${id}/generate-report`,
      {},
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to generate report");
  }

  async deleteRecordV2(id: string): Promise<void> {
    const response = await this.httpCoreService.delete(
      `/feasibility-records/${id}`,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return;
    }
    throw new Error(response.message || "Failed to delete record");
  }

  // ─── V2: Response Reports ───────────────────────────────────────────────────

  async listResponseReports(params?: {
    studyId?: string;
    page?: number;
    limit?: number;
  }): Promise<FeasibilityReportListResponse> {
    const query = new URLSearchParams();
    if (params?.studyId) {
      query.set("studyId", params.studyId);
    }
    if (params?.page) {
      query.set("page", String(params.page));
    }
    if (params?.limit) {
      query.set("limit", String(params.limit));
    }
    const url = `/feasibility-reports${query.toString() ? `?${query.toString()}` : ""}`;
    const response =
      await this.httpCoreService.get<FeasibilityReportListResponse>(url);
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to fetch response reports");
  }

  async getResponseReport(id: string): Promise<FeasibilityResponseReport> {
    const response = await this.httpCoreService.get<FeasibilityResponseReport>(
      `/feasibility-reports/${id}`,
    );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to fetch response report");
  }

  async fileReportToETMF(
    id: string,
    file?: File,
  ): Promise<FeasibilityResponseReport> {
    const response = file
      ? await this.httpCoreService.postForm<FeasibilityResponseReport>(
          `/feasibility-reports/${id}/file-to-etmf-upload`,
          (() => {
            const formData = new FormData();
            formData.append("document", file);
            return formData;
          })(),
        )
      : await this.httpCoreService.patch<FeasibilityResponseReport>(
          `/feasibility-reports/${id}/file-to-etmf`,
          {},
        );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to file report to eTMF");
  }

  async replaceReportVersion(
    id: string,
    file: File,
    reason: string,
  ): Promise<FeasibilityResponseReport> {
    const formData = new FormData();
    formData.append("document", file);
    formData.append("reason", reason);
    const response =
      await this.httpCoreService.postForm<FeasibilityResponseReport>(
        `/feasibility-reports/${id}/replace-version`,
        formData,
      );
    if (response.statusCode >= 200 && response.statusCode < 300) {
      return response.data;
    }
    throw new Error(response.message || "Failed to replace report version");
  }

  // ─── Public (unauthenticated) ──────────────────────────────────────────────

  async getPublicRecord(recordId: string): Promise<FeasibilityPublicRecord> {
    const resp = await SiteFeasibilityRepository.publicClient.get<{
      statusCode: number;
      message: string;
      data: FeasibilityPublicRecord;
    }>(`/feasibility-records/public/${recordId}`);
    const body = resp.data;
    if (body.statusCode >= 200 && body.statusCode < 300) {
      return body.data;
    }
    throw new Error(body.message || "Failed to fetch questionnaire");
  }

  async submitQuestionnaire(
    recordId: string,
    data: SubmitQuestionnaireRequest,
  ): Promise<FeasibilityRecordV2> {
    const resp = await SiteFeasibilityRepository.publicClient.post<{
      statusCode: number;
      message: string;
      data: FeasibilityRecordV2;
    }>(`/feasibility-records/${recordId}/submit`, data);
    const body = resp.data;
    if (body.statusCode >= 200 && body.statusCode < 300) {
      return body.data;
    }
    throw new Error(body.message || "Failed to submit questionnaire");
  }
}
